<script setup lang="ts">
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { onBeforeRouteLeave } from 'vue-router';
import AddressForm from '@/components/AddressForm.vue';
import { computed, inject, nextTick, onMounted, ref, type Ref } from 'vue';
import { RealestateCollection } from '@/models/Realestate';
import { RelationshipToPerson, RelationshipToPersonCollection, ROLE, TYPE } from '@/models/RelationshipToPerson';
import { createModel } from '@kp/js-model';
import PrevNextBtnBar from '@/components/PrevNextBtnBar.vue';
import { hasPpaReachedSubmitRoute } from '@/helpers/tools';
import { createValidator, createValidators, validateNotNull } from '@kp/geofy-vuejs-lib/tools/model_validators.js';
import YesNoRadios from '@/components/YesNoRadios.vue';
import { useBrowserStorage } from '@kp/geofy-vuejs-lib/tools/browser_local_storage.js';
import { useAuthUser } from '@/helpers/globals';

const saving = inject('saving') as Ref<boolean>;
const saveGeschaeft = inject('saveGeschaeft') as () => Promise<void>;
const authUser = useAuthUser();
const { setStorageValue } = useBrowserStorage('eplan');
import { generateGemeindeRelationship } from '@/composables/useGenerateGemeindeRelationship';


const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

const emit = defineEmits<{
    'geschaeft-changed': [section: string | null];
    next: [];
    prev: [];
}>();

// injected from PlanungsgeschaeftEditView
const readonly = inject('readonly') as Ref<boolean>;

const forceValidation = hasPpaReachedSubmitRoute(props.geschaeft);

/**
 * When this route is about to leave, we inform the parent so that it
 * has a chance to save the Geschaeft first.
 */
onBeforeRouteLeave(() => {
    onGeschaeftChanged();
    saveRelationshipToPerson();
    return true;
});

function onReset() {
    emit('prev');
}

async function onNextStep() {
    emit('next');
}

async function mutateGeschaeft(field: string, value: unknown) {
    if (!readonly.value) {
        props.geschaeft.set(field, value);
        loading.value = true;
        await saveGeschaeft();
        loading.value = false;
    }
}

/**
 * Called when this component did changes on the Geschaeft model instance.
 * It is in the parent's responsibility to save the model.
 */
function onGeschaeftChanged() {
    emit('geschaeft-changed', 'beteiligte');
}

const parzellen: Ref<RealestateCollection | null> = ref(null);
async function loadParzellen() {
    const collection = new RealestateCollection();
    collection.setQueryParam('dossier_id', props.geschaeft.id);
    collection.setQueryParam('is_neighbour', false);
    await collection.query();
    parzellen.value = collection;
}

const applicants: Ref<RelationshipToPerson[]> = ref([]);
const projectAuthors: Ref<RelationshipToPerson[]> = ref([]);
async function loadRelationshipToPerson() {
    const collection = new RelationshipToPersonCollection();
    collection.setQueryParam('filter', [
        {
            id: 'planning_permission_application_id',
            property: 'planning_permission_application_id',
            value: props.geschaeft.id
        }
    ]);
    await collection.query();
    applicants.value = collection.filter((rtp) => rtp.role === ROLE.APPLICANT);
    projectAuthors.value = collection.filter((rtp) => rtp.role === ROLE.PROJECT_AUTHOR);
}


const loading: Ref<boolean> = ref(false);

async function deleteRelationship(collection: RelationshipToPerson[], value: RelationshipToPerson) {
    if (readonly.value) {
        return;
    }
    await value.destroy();
    collection.splice(collection.indexOf(value), 1);
}

const addRelationshipEdit: Ref<boolean> = ref(false);
const newItems: Ref<RelationshipToPerson[]> = ref([]);

async function addRelationship(role: string) {
    if (readonly.value) {
        return;
    }
    const newItem = createModel(RelationshipToPerson, {
        role: role,
        planning_permission_application_id: props.geschaeft.id,
        address_type: TYPE.ORGANISATION
    });
    addRelationshipEdit.value = true;
    switch (role) {
        case ROLE.APPLICANT:
            applicants.value.push(newItem);
            break;
        case ROLE.PROJECT_AUTHOR:
            projectAuthors.value.push(newItem);
            break;
    }
    await saveRelationshipToPerson();
}

async function saveRelationshipToPerson() {
    if (readonly.value) {
        return;
    }
    const prom: Promise<RelationshipToPerson>[] = [];
    newItems.value?.forEach((rtp) => prom.push(rtp.save()));
    applicants.value?.forEach((rtp) => prom.push(rtp.save()));
    projectAuthors.value?.forEach((rtp) => prom.push(rtp.save()));

    if (applicants.value.length > 0) {
        const rtp = applicants.value[0];
        const stelle_id = authUser?.active_role_stelle_id;
        if (rtp.email) {
            setStorageValue('stelle_' + stelle_id + '_email', rtp.email);
        }
        if (rtp.phone) {
            setStorageValue('stelle_' + stelle_id + '_phone', rtp.phone);
        }
    }

    await Promise.all(prom);
}

const {
    validators,
    validateAll,
    enableAll: enableAllValidators
} = createValidators({
    landowner_equals_applicant: createValidator(
        computed(() => props.geschaeft.landowner_equals_applicant),
        [validateNotNull],
        { initialValidate: false, enabled: true }
    ),
    project_author_equals_applicant: createValidator(
        computed(() => props.geschaeft.project_author_equals_applicant),
        [validateNotNull],
        { initialValidate: false, enabled: true }
    )
});

function updateEqualsApplicant(type: string, event: boolean | null | undefined) {
    mutateGeschaeft(type + '_equals_applicant', event);
    switch (type) {
        case 'project_author':
            if (event === true) {
                projectAuthors.value.forEach(async (r) => await r.destroy());
                projectAuthors.value = [];
            } else if (!projectAuthors.value || projectAuthors.value.length < 1) {
                projectAuthors.value.push(
                    createModel(RelationshipToPerson, {
                        role: ROLE.PROJECT_AUTHOR,
                        planning_permission_application_id: props.geschaeft.id,
                        address_type: TYPE.PERSON
                    })
                );
            }
            break;
    }
    saveRelationshipToPerson();
}

onMounted(async () => {
    loadParzellen();
    await loadRelationshipToPerson();
    if (applicants.value.length === 0) {
        await generateGemeindeRelationship(props?.geschaeft?.id);
        await loadRelationshipToPerson();

    }
    nextTick(() => {
        enableAllValidators();
        if (hasPpaReachedSubmitRoute(props.geschaeft)) {
            validateAll();
        }
    });
});
</script>

<template>
    <div>
        <div>
            <h4 class="ktg-title-04 ktg-inner-box-padding mb-1_5rem">{{ $t('beteiligte') }}</h4>
            <fieldset class="ktg-fieldset ktg-form-area-maxwidth py-3_5rem px-6rem">
                <!-- GEMEINDE -->
                <h5 class="ktg-title-05 ktg-inner-box-padding">{{ $t('gemeinde') }}</h5>
                <div v-for="(relationship) in applicants" :key="relationship.id || undefined">
                    <AddressForm
                        :organisation-title="$t('gemeinde')"
                        :organisation-placeholder="$t('gemeindename')"
                        :model-value="relationship"
                        :deletable="false"
                        :add-allowed="false"
                        :readonly="readonly || loading"
                        :force-validation="forceValidation"
                        organisationOnly
                        hideEndLine
                    ></AddressForm>
                </div>

                <!-- PROJEKTVERFASSER -->
                <h5 class="ktg-title-05 mt-3_5rem ktg-inner-box-padding">{{ $t('project_author') }}</h5>
                <ktg-line class="mt-0_5rem mb-1rem"></ktg-line>
                <div class="gap-1_5rem ktg-inner-box-padding">
                    <span>{{ $t('project_author_same_as_applicant') }} *</span>
                    <YesNoRadios
                        class="mt-1rem"
                        name="project_author_equals_applicant"
                        :readonly="readonly || loading"
                        :model-value="props.geschaeft.project_author_equals_applicant"
                        :error="validators.project_author_equals_applicant.msg.value"
                        :yes-text="$t('no')"
                        :no-text="$t('yes')"
                        @update:model-value="updateEqualsApplicant('project_author', $event)"
                    />
                </div>
                <template v-if="props.geschaeft.project_author_equals_applicant === false">
                    <div v-for="(relationship, relationshipIdx) in projectAuthors" :key="relationship.id || undefined">
                        <AddressForm
                            :title="
                                projectAuthors.length > 1 && relationshipIdx > 0
                                    ? $t('project_author') + ' ' + (relationshipIdx + 1)
                                    : null
                            "
                            :type-label="$t('project_author_is')"
                            :model-value="relationship"
                            :deletable="projectAuthors.length > 1"
                            :readonly="readonly || loading"
                            :force-validation="forceValidation"
                            org-contact-optional
                            :add-allowed="projectAuthors.length === relationshipIdx + 1"
                            @delete="deleteRelationship(projectAuthors, relationship)"
                            @add="addRelationship(ROLE.PROJECT_AUTHOR)"
                        ></AddressForm>
                    </div>
                </template>

                <ktg-line class="my-1rem"></ktg-line>
                <PrevNextBtnBar
                    :back-text="$t('back')"
                    :next-text="$t('to_next_step')"
                    :disabled="saving"
                    @next="onNextStep"
                    @back="onReset"
                />
            </fieldset>
        </div>
    </div>
</template>

<style scoped lang="scss">
fieldset {
    width: 53rem;
}
</style>
